/**
 * Transform CKB data into React Flow nodes and edges
 */

/**
 * Role colors mapping - maps CKB's inferredRole to display colors
 * CKB roles: test, entrypoint, api, ui, data, util, config, core
 */
const ROLE_COLORS = {
  api: { color: '#6b7280', label: 'API' },           // Gray - backend/API layer
  ui: { color: '#3b82f6', label: 'UI' },             // Blue - frontend/UI layer
  data: { color: '#22c55e', label: 'Data' },         // Green - database/storage
  util: { color: '#8b5cf6', label: 'Util' },         // Purple - utilities/shared
  config: { color: '#f97316', label: 'Config' },     // Orange - configuration
  test: { color: '#eab308', label: 'Test' },         // Yellow - test files
  entrypoint: { color: '#ec4899', label: 'Entry' },  // Pink - entry points
  core: { color: '#64748b', label: 'Core' },         // Slate - core/internal
};

/**
 * Fallback boundary inference patterns (used when CKB doesn't provide role)
 */
const BOUNDARY_PATTERNS = [
  { pattern: /^(web|frontend|ui|client|app|components?|views?)$/i, layer: 'ui', color: '#3b82f6', label: 'UI' },
  { pattern: /^(api|server|backend|routes?|handlers?|controllers?)$/i, layer: 'api', color: '#6b7280', label: 'API' },
  { pattern: /^(database|db|storage|models?|schema|repository|store)$/i, layer: 'data', color: '#22c55e', label: 'Data' },
  { pattern: /^(utils?|helpers?|common|shared|lib)$/i, layer: 'util', color: '#8b5cf6', label: 'Util' },
  { pattern: /^(config|conf|settings|env)$/i, layer: 'config', color: '#f97316', label: 'Config' },
  { pattern: /^(tests?|spec|__tests__)$/i, layer: 'test', color: '#eab308', label: 'Test' },
  { pattern: /^(cmd|bin|main|app|entry)$/i, layer: 'entrypoint', color: '#ec4899', label: 'Entry' },
  { pattern: /^(hooks?|contexts?|providers?)$/i, layer: 'util', color: '#8b5cf6', label: 'Util' },
  { pattern: /^(auth|security|login|session)$/i, layer: 'api', color: '#ef4444', label: 'Auth' },
  { pattern: /^(notification|email|messaging)$/i, layer: 'api', color: '#f97316', label: 'Notify' },
];

/**
 * Get boundary info from CKB role or infer from path
 * @param {string} role - CKB's inferredRole field (optional)
 * @param {string} path - Directory path (fallback)
 */
export function getBoundary(role, path) {
  // If CKB provided a role, use it
  if (role && ROLE_COLORS[role]) {
    return {
      layer: role,
      color: ROLE_COLORS[role].color,
      label: ROLE_COLORS[role].label,
      source: 'ckb'
    };
  }

  // Fallback: infer from path
  return inferBoundary(path);
}

/**
 * Infer the semantic layer/boundary from a directory path (fallback)
 */
export function inferBoundary(path) {
  if (!path) return { layer: 'core', color: '#64748b', label: 'Core', source: 'inferred' };

  const parts = path.split('/').filter(Boolean);

  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i];
    for (const { pattern, layer, color, label } of BOUNDARY_PATTERNS) {
      if (pattern.test(part)) {
        return { layer, color, label, source: 'inferred' };
      }
    }
  }

  return { layer: 'core', color: '#64748b', label: 'Core', source: 'inferred' };
}

/**
 * Get the parent path of a directory path
 * e.g., "src/providers" → "src", "web/src/hooks" → "web/src"
 */
function getParentPath(path) {
  if (!path) return null;
  const parts = path.split('/');
  if (parts.length <= 1) return null;
  return parts.slice(0, -1).join('/');
}

/**
 * Detect cycles in a directed graph using Tarjan's algorithm
 * Returns array of cycles (each cycle is an array of node IDs)
 */
function detectCycles(nodes, edges) {
  const graph = new Map();
  const nodeIds = new Set(nodes.map(n => n.id));

  // Build adjacency list
  nodeIds.forEach(id => graph.set(id, []));
  edges.forEach(edge => {
    if (nodeIds.has(edge.source) && nodeIds.has(edge.target)) {
      graph.get(edge.source)?.push(edge.target);
    }
  });

  // Find all strongly connected components (SCCs)
  let index = 0;
  const stack = [];
  const onStack = new Set();
  const indices = new Map();
  const lowlinks = new Map();
  const sccs = [];

  function strongconnect(v) {
    indices.set(v, index);
    lowlinks.set(v, index);
    index++;
    stack.push(v);
    onStack.add(v);

    const neighbors = graph.get(v) || [];
    for (const w of neighbors) {
      if (!indices.has(w)) {
        strongconnect(w);
        lowlinks.set(v, Math.min(lowlinks.get(v), lowlinks.get(w)));
      } else if (onStack.has(w)) {
        lowlinks.set(v, Math.min(lowlinks.get(v), indices.get(w)));
      }
    }

    if (lowlinks.get(v) === indices.get(v)) {
      const scc = [];
      let w;
      do {
        w = stack.pop();
        onStack.delete(w);
        scc.push(w);
      } while (w !== v);

      // Only include SCCs with more than one node (actual cycles)
      if (scc.length > 1) {
        sccs.push(scc);
      }
    }
  }

  for (const v of nodeIds) {
    if (!indices.has(v)) {
      strongconnect(v);
    }
  }

  return sccs;
}

/**
 * Calculate fan-in (incoming edges) and fan-out (outgoing edges) for each node
 */
function calculateCoupling(nodes, edges) {
  const fanIn = new Map();
  const fanOut = new Map();
  const nodeIds = new Set(nodes.map(n => n.id));

  // Initialize
  nodeIds.forEach(id => {
    fanIn.set(id, 0);
    fanOut.set(id, 0);
  });

  // Count edges
  edges.forEach(edge => {
    if (nodeIds.has(edge.source) && nodeIds.has(edge.target)) {
      fanOut.set(edge.source, (fanOut.get(edge.source) || 0) + 1);
      fanIn.set(edge.target, (fanIn.get(edge.target) || 0) + 1);
    }
  });

  return { fanIn, fanOut };
}

/**
 * Determine coupling level based on fan-in count
 */
function getCouplingLevel(fanIn) {
  if (fanIn <= 1) return 'low';
  if (fanIn <= 3) return 'medium';
  if (fanIn <= 6) return 'high';
  return 'critical';
}

/**
 * Transform architecture data into React Flow format
 * Supports both module-level (v7) and directory-level (v8) data
 * @param {object} architectureData - Raw CKB architecture data
 * @param {string} archMode - 'structure' or 'dependencies'
 */
export function transformArchitecture(architectureData, archMode = 'structure') {
  const nodes = [];
  const edges = [];

  if (!architectureData) return { nodes, edges };

  // Parse if string
  let parsed;
  try {
    parsed = typeof architectureData === 'string'
      ? JSON.parse(architectureData)
      : architectureData;
  } catch (e) {
    console.error('Failed to parse architecture data:', e);
    return { nodes, edges };
  }

  // Handle different response formats from CKB
  const data = parsed.data || parsed;

  // CKB v8+ returns directories for directory granularity
  const directories = data.directories || [];
  const modules = data.modules || [];

  // Use directories if available (CKB v8+), otherwise fall back to modules
  const items = directories.length > 0 ? directories : modules;
  const isDirectoryMode = directories.length > 0;

  // Create nodes from directories or modules
  items.forEach((item, idx) => {
    const itemId = item.moduleId || item.path || `item-${idx}`;
    const pathParts = (item.path || '').split('/');
    const displayName = pathParts[pathParts.length - 1] || item.name || 'Root';

    // Get semantic boundary from CKB role or infer from path
    const boundary = getBoundary(item.role, item.path);

    nodes.push({
      id: itemId,
      type: isDirectoryMode ? 'file' : 'module',
      position: { x: 0, y: 0 }, // Layout will set this
      data: {
        label: displayName,
        nodeType: isDirectoryMode ? 'file' : 'module',
        fileCount: item.fileCount,
        symbolCount: item.symbolCount,
        language: item.language,
        path: item.path,
        relativePath: item.path,
        isDirectory: isDirectoryMode,
        loc: item.loc, // Lines of code (v8+)
        hasIndexFile: item.hasIndexFile,
        // Semantic role from CKB (v8+) or inferred
        role: item.role || boundary.layer,
        boundary,
        // Metrics from CKB v8+ includeMetrics option
        metrics: item.metrics ? {
          loc: item.metrics.loc || 0,
          avgComplexity: item.metrics.avgComplexity || 0,
          maxComplexity: item.metrics.maxComplexity || 0,
          churn: item.metrics.churn30d || 0,
          lastModified: item.metrics.lastModified || null
        } : null
      }
    });
  });

  // Synthesize missing intermediate nodes so the directory tree is fully connected.
  // CKB's dependency edges reference paths that may not appear in the directories array,
  // and shallow depth cuts off deeper directories. Walk every known path up to root and
  // create synthetic nodes for any gaps.
  if (isDirectoryMode) {
    const existingIds = new Set(nodes.map(n => n.id));
    const allPaths = new Set(nodes.map(n => n.data.path).filter(Boolean));

    // Collect directory-level paths referenced by dependency edges.
    // Some dependency targets point to individual files (e.g. web/src/api/client);
    // we only want the directory portion, so strip any path whose last segment
    // looks like a file (contains a dot) or matches a known file pattern.
    const depGraph = data.dependencyGraph || data.directoryDependencies || [];
    depGraph.forEach(dep => {
      if (dep.from) allPaths.add(dep.from);
      if (dep.to) {
        // Check if 'to' looks like a file path (last segment has a dot extension)
        const lastSeg = dep.to.split('/').pop();
        if (lastSeg && lastSeg.includes('.')) {
          // It's a file — add its parent directory instead
          const dirPath = getParentPath(dep.to);
          if (dirPath) allPaths.add(dirPath);
        } else {
          allPaths.add(dep.to);
        }
      }
    });

    // Walk up parent chains to discover all intermediate paths needed
    const neededPaths = new Set();
    for (const p of allPaths) {
      let current = p;
      while (current) {
        if (!existingIds.has(current)) neededPaths.add(current);
        current = getParentPath(current);
      }
    }

    // Create synthetic nodes for missing paths
    for (const path of neededPaths) {
      const parts = path.split('/');
      const displayName = parts[parts.length - 1] || path;
      const boundary = getBoundary(null, path);
      nodes.push({
        id: path,
        type: 'file',
        position: { x: 0, y: 0 },
        data: {
          label: displayName,
          nodeType: 'file',
          fileCount: 0,
          symbolCount: 0,
          language: 'unknown',
          path,
          relativePath: path,
          isDirectory: true,
          loc: 0,
          role: boundary.layer,
          boundary,
          metrics: null,
          isSynthetic: true,
        }
      });
      existingIds.add(path);
    }

  }

  // Tag nodes as module fallback when CKB returned module-level data for a hierarchy/files request
  if (!isDirectoryMode && archMode === 'hierarchy') {
    nodes.forEach(node => {
      node.data.isModuleFallback = true;
    });
  }

  // For 'hierarchy' mode (Files view), skip dependency edges and use only hierarchical edges
  const useHierarchyOnly = archMode === 'hierarchy';

  // Create edges from dependency graph (if CKB provides them)
  // Skip for hierarchy mode - we want tree structure, not dependencies
  if (!useHierarchyOnly) {
    const depGraph = data.dependencyGraph || data.directoryDependencies || [];
    depGraph.forEach((dep, idx) => {
      if (dep.from && dep.to) {
        // Calculate stroke width based on import count (1-4 scale)
        const importCount = dep.importCount || 1;
        const strokeWidth = Math.min(1 + Math.log2(importCount + 1), 4);

        edges.push({
          id: `edge-dep-${idx}`,
          source: dep.from,
          target: dep.to,
          type: 'smoothstep',
          animated: dep.isExternal || false,
          selectable: true,
          focusable: true,
          interactionWidth: 20, // Make edges easier to click
          style: {
            stroke: dep.isExternal ? '#94a3b8' : '#3b82f6',
            strokeWidth: Math.round(strokeWidth)
          },
          markerEnd: { type: 'arrowclosed' },
          // Store dependency data for tooltips/labels
          data: {
            importCount: dep.importCount,
            symbols: dep.symbols || []
          }
        });
      }
    });
  }

  // Create hierarchical edges based on directory structure
  // This infers parent-child relationships from paths (e.g., src → src/providers)
  // Used for Files view (hierarchy mode) or as fallback when no dependency edges exist
  if (isDirectoryMode && (useHierarchyOnly || edges.length === 0)) {
    const nodeIds = new Set(nodes.map(n => n.id));

    nodes.forEach((node, idx) => {
      const path = node.data.path;
      if (!path) return;

      const parentPath = getParentPath(path);
      if (parentPath && nodeIds.has(parentPath)) {
        edges.push({
          id: `edge-hierarchy-${idx}`,
          source: parentPath,
          target: path,
          type: 'smoothstep',
          style: { stroke: '#8b5cf6', strokeWidth: 2 }, // Purple for hierarchy
          markerEnd: { type: 'arrowclosed' },
        });
      }
    });
  }

  // For dependencies mode, add coupling metrics and cycle detection
  if (archMode === 'dependencies') {
    // Calculate fan-in/fan-out for each node
    const { fanIn, fanOut } = calculateCoupling(nodes, edges);

    // Detect cycles
    const cycles = detectCycles(nodes, edges);
    const nodesInCycles = new Set(cycles.flat());

    // Create a set of edges that are part of cycles
    const cycleEdges = new Set();
    cycles.forEach(cycle => {
      for (let i = 0; i < cycle.length; i++) {
        const from = cycle[i];
        const to = cycle[(i + 1) % cycle.length];
        // Mark both directions since we're looking at SCCs
        cycleEdges.add(`${from}->${to}`);
        cycleEdges.add(`${to}->${from}`);
      }
    });

    // Enhance nodes with coupling data
    nodes.forEach(node => {
      const nodeId = node.id;
      const inCount = fanIn.get(nodeId) || 0;
      const outCount = fanOut.get(nodeId) || 0;

      node.data.coupling = {
        fanIn: inCount,
        fanOut: outCount,
        level: getCouplingLevel(inCount),
        inCycle: nodesInCycles.has(nodeId)
      };
    });

    // Build a map of node ID to boundary layer for cross-boundary detection
    const nodeBoundaryMap = new Map();
    nodes.forEach(node => {
      nodeBoundaryMap.set(node.id, node.data.boundary?.layer || 'core');
    });

    // Enhance edges with cycle and cross-boundary information
    edges.forEach(edge => {
      const edgeKey = `${edge.source}->${edge.target}`;
      const isCycleEdge = cycleEdges.has(edgeKey);

      // Check if edge crosses semantic boundaries
      const sourceLayer = nodeBoundaryMap.get(edge.source);
      const targetLayer = nodeBoundaryMap.get(edge.target);
      const isCrossBoundary = sourceLayer && targetLayer && sourceLayer !== targetLayer;

      edge.data = {
        ...edge.data,
        isCycle: isCycleEdge,
        isCrossBoundary,
        sourceLayer,
        targetLayer
      };

      if (isCycleEdge) {
        edge.style = {
          ...edge.style,
          stroke: '#ef4444', // Red for cycle edges
          strokeWidth: 3
        };
        edge.animated = true;
        edge.label = 'cycle';
        edge.labelStyle = { fill: '#ef4444', fontSize: 10, fontWeight: 600 };
        edge.labelBgStyle = { fill: '#1e1035', fillOpacity: 0.9 };
        edge.labelBgPadding = [4, 2];
        edge.labelBgBorderRadius = 3;
      }
    });
  }

  return { nodes, edges };
}

/**
 * Transform call graph data into React Flow format
 */
export function transformCallGraph(callGraphData, rootSymbolId) {
  const nodes = [];
  const edges = [];
  const seen = new Set();

  if (!callGraphData) return { nodes, edges };

  // Parse if string
  let parsed;
  try {
    parsed = typeof callGraphData === 'string'
      ? JSON.parse(callGraphData)
      : callGraphData;
  } catch (e) {
    console.error('Failed to parse call graph data:', e);
    return { nodes, edges };
  }

  const data = parsed.data || parsed;

  // Root node (the symbol we're exploring)
  const rootSymbol = data.symbol || data.root;
  if (rootSymbol) {
    const rootId = rootSymbol.id || rootSymbolId || 'root';
    nodes.push({
      id: rootId,
      type: 'function',
      position: { x: 0, y: 0 },
      data: {
        label: rootSymbol.name || 'Unknown',
        nodeType: 'function',
        kind: rootSymbol.kind || 'function',
        signature: rootSymbol.signature,
        fileName: rootSymbol.file || rootSymbol.location?.file,
        symbolId: rootId,
        isRoot: true,
      }
    });
    seen.add(rootId);
  }

  // Callers (nodes that call this symbol)
  const callers = data.callers || [];
  callers.forEach((caller, idx) => {
    const callerId = caller.symbol?.id || caller.id || `caller-${idx}`;
    const callerSymbol = caller.symbol || caller;

    if (!seen.has(callerId)) {
      nodes.push({
        id: callerId,
        type: 'function',
        position: { x: 0, y: 0 },
        data: {
          label: callerSymbol.name || 'Unknown',
          nodeType: 'function',
          kind: callerSymbol.kind || 'function',
          fileName: callerSymbol.file || caller.location?.file,
          symbolId: callerId,
          direction: 'caller',
        }
      });
      seen.add(callerId);
    }

    // Edge from caller to root
    const rootId = rootSymbol?.id || rootSymbolId || 'root';
    edges.push({
      id: `edge-caller-${idx}`,
      source: callerId,
      target: rootId,
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#22c55e', strokeWidth: 2 }, // Green for incoming calls
      markerEnd: { type: 'arrowclosed' },
    });
  });

  // Callees (nodes this symbol calls)
  const callees = data.callees || [];
  callees.forEach((callee, idx) => {
    const calleeId = callee.symbol?.id || callee.id || `callee-${idx}`;
    const calleeSymbol = callee.symbol || callee;

    if (!seen.has(calleeId)) {
      nodes.push({
        id: calleeId,
        type: 'function',
        position: { x: 0, y: 0 },
        data: {
          label: calleeSymbol.name || 'Unknown',
          nodeType: 'function',
          kind: calleeSymbol.kind || 'function',
          fileName: calleeSymbol.file || callee.location?.file,
          symbolId: calleeId,
          direction: 'callee',
        }
      });
      seen.add(calleeId);
    }

    // Edge from root to callee
    const rootId = rootSymbol?.id || rootSymbolId || 'root';
    edges.push({
      id: `edge-callee-${idx}`,
      source: rootId,
      target: calleeId,
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#f59e0b', strokeWidth: 2 }, // Orange for outgoing calls
      markerEnd: { type: 'arrowclosed' },
    });
  });

  return { nodes, edges };
}

/**
 * Transform file/module data into file dependency graph
 */
export function transformFileDependencies(architectureData) {
  const nodes = [];
  const edges = [];
  const seenFiles = new Set();

  if (!architectureData) return { nodes, edges };

  // Parse if string
  let parsed;
  try {
    parsed = typeof architectureData === 'string'
      ? JSON.parse(architectureData)
      : architectureData;
  } catch (e) {
    console.error('Failed to parse file dependencies data:', e);
    return { nodes, edges };
  }

  const data = parsed.data || parsed;
  const modules = data.modules || [];

  // Create file nodes from modules (each module represents a directory/package)
  modules.forEach((mod, modIdx) => {
    const moduleId = mod.moduleId || mod.path || `module-${modIdx}`;
    const modulePath = mod.path || '';

    nodes.push({
      id: moduleId,
      type: 'file',
      position: { x: 0, y: 0 },
      data: {
        label: mod.name || modulePath.split('/').pop() || 'Root',
        nodeType: 'file',
        relativePath: modulePath,
        fileName: modulePath.split('/').pop(),
        fileCount: mod.fileCount,
        isDirectory: true,
      }
    });
    seenFiles.add(moduleId);
  });

  // Create edges between modules based on imports/dependencies
  const depGraph = data.dependencyGraph || [];
  depGraph.forEach((dep, idx) => {
    if (dep.from && dep.to && seenFiles.has(dep.from) && seenFiles.has(dep.to)) {
      edges.push({
        id: `edge-file-${idx}`,
        source: dep.from,
        target: dep.to,
        type: 'smoothstep',
        style: {
          stroke: '#64748b',
          strokeWidth: 2
        },
        markerEnd: { type: 'arrowclosed' },
      });
    }
  });

  return { nodes, edges };
}

/**
 * Transform file tree data (from /sessions/:id/files) into React Flow graph nodes/edges.
 * Used as a fallback when CKB can't provide directory-level architecture data.
 * @param {Array} tree - File tree array from the file tree API
 * @returns {Object} - { nodes, edges } for React Flow
 */
export function transformFileTree(tree) {
  const nodes = [];
  const edges = [];

  if (!tree || !Array.isArray(tree)) return { nodes, edges };

  function walk(items, parentPath) {
    for (const item of items) {
      if (item.type !== 'directory') continue;

      const boundary = getBoundary(null, item.path);
      const fileCount = item.children
        ? item.children.filter(c => c.type === 'file').length
        : 0;
      const dirCount = item.children
        ? item.children.filter(c => c.type === 'directory').length
        : 0;

      nodes.push({
        id: item.path,
        type: 'file',
        position: { x: 0, y: 0 },
        data: {
          label: item.name,
          nodeType: 'file',
          fileCount,
          symbolCount: 0,
          path: item.path,
          relativePath: item.path,
          isDirectory: true,
          role: boundary.layer,
          boundary,
          metrics: null,
          isFileTreeFallback: true,
        }
      });

      if (parentPath) {
        edges.push({
          id: `edge-ft-${parentPath}-${item.path}`,
          source: parentPath,
          target: item.path,
          type: 'smoothstep',
          style: { stroke: '#8b5cf6', strokeWidth: 2 },
          markerEnd: { type: 'arrowclosed' },
        });
      }

      if (item.children) {
        walk(item.children, item.path);
      }
    }
  }

  walk(tree, null);
  return { nodes, edges };
}

/**
 * Check if a path is a direct child of a parent path
 * e.g., isDirectChild("src/server", "src") => true
 *       isDirectChild("src/server/routes", "src") => false
 */
function isDirectChild(path, parentPath) {
  if (!parentPath) {
    // At root level, direct children are top-level directories (no "/" in path)
    return path && !path.includes('/');
  }
  if (!path.startsWith(parentPath + '/')) return false;
  const remainder = path.slice(parentPath.length + 1);
  return !remainder.includes('/');
}

/**
 * Get direct children paths from a list of all paths
 */
function getDirectChildPaths(allPaths, parentPath) {
  return allPaths.filter(p => isDirectChild(p, parentPath));
}

/**
 * Filter nodes and edges to show only direct children of scopePath
 * Creates collapsed "External" nodes for dependencies outside the scope
 * @param {Array} nodes - All nodes
 * @param {Array} edges - All edges
 * @param {string|null} scopePath - Current drill-down path (null = root)
 * @param {Array} allNodes - Reference to ALL nodes for checking children
 * @returns {Object} - { nodes, edges } filtered for the current scope
 */
export function filterToDrillScope(nodes, edges, scopePath, allNodes = nodes) {
  // Get all paths to determine what children exist
  const allPaths = allNodes.map(n => n.data?.path || n.id);

  // Get direct children of current scope
  const directChildPaths = new Set(getDirectChildPaths(allPaths, scopePath));

  // Filter nodes to only direct children
  const visibleNodes = nodes.filter(n => {
    const path = n.data?.path || n.id;
    return directChildPaths.has(path);
  });

  // Mark each node with hasChildren (can be drilled into)
  visibleNodes.forEach(node => {
    const nodePath = node.data?.path || node.id;
    // Check if any path is a child of this node's path
    const hasChildren = allPaths.some(p =>
      p !== nodePath && p.startsWith(nodePath + '/')
    );
    node.data.hasChildren = hasChildren;
  });

  const visibleNodeIds = new Set(visibleNodes.map(n => n.id));

  // Track external dependencies
  const externalDependencies = new Map(); // path -> { symbols, count }
  const externalDependents = new Map(); // path -> { symbols, count }

  // Filter edges - keep internal edges, collect external ones
  const internalEdges = [];

  edges.forEach(edge => {
    const sourceInScope = visibleNodeIds.has(edge.source);
    const targetInScope = visibleNodeIds.has(edge.target);

    if (sourceInScope && targetInScope) {
      // Both ends are in scope - keep the edge
      internalEdges.push(edge);
    } else if (sourceInScope && !targetInScope) {
      // Source is in scope, target is external (outgoing dependency)
      const existing = externalDependencies.get(edge.target) || { symbols: [], count: 0 };
      existing.symbols.push(...(edge.data?.symbols || []));
      existing.count += edge.data?.importCount || 1;
      externalDependencies.set(edge.target, existing);
    } else if (!sourceInScope && targetInScope) {
      // Target is in scope, source is external (incoming dependent)
      const existing = externalDependents.get(edge.source) || { symbols: [], count: 0 };
      existing.symbols.push(...(edge.data?.symbols || []));
      existing.count += edge.data?.importCount || 1;
      externalDependents.set(edge.source, existing);
    }
  });

  // Create collapsed external nodes if there are external connections
  const resultNodes = [...visibleNodes];
  const resultEdges = [...internalEdges];

  if (externalDependencies.size > 0) {
    const extDepNodeId = '__external_deps__';
    resultNodes.push({
      id: extDepNodeId,
      type: 'external',
      position: { x: 0, y: 0 },
      data: {
        label: `External Dependencies (${externalDependencies.size})`,
        nodeType: 'external',
        isExternal: true,
        direction: 'outgoing',
        externalPaths: Array.from(externalDependencies.entries()).map(([path, data]) => ({
          path,
          name: path.split('/').pop(),
          count: data.count,
          symbols: [...new Set(data.symbols)].slice(0, 5)
        })),
        totalCount: externalDependencies.size
      }
    });

    // Add edges from visible nodes to the external deps node
    const sourcesWithExternalDeps = new Set();
    edges.forEach(edge => {
      if (visibleNodeIds.has(edge.source) && externalDependencies.has(edge.target)) {
        sourcesWithExternalDeps.add(edge.source);
      }
    });
    sourcesWithExternalDeps.forEach(source => {
      resultEdges.push({
        id: `edge-to-ext-${source}`,
        source,
        target: extDepNodeId,
        type: 'smoothstep',
        style: { stroke: '#94a3b8', strokeWidth: 2, strokeDasharray: '5,5' },
        markerEnd: { type: 'arrowclosed' },
        data: { isExternal: true }
      });
    });
  }

  if (externalDependents.size > 0) {
    const extDeptNodeId = '__external_dependents__';
    resultNodes.push({
      id: extDeptNodeId,
      type: 'external',
      position: { x: 0, y: 0 },
      data: {
        label: `External Dependents (${externalDependents.size})`,
        nodeType: 'external',
        isExternal: true,
        direction: 'incoming',
        externalPaths: Array.from(externalDependents.entries()).map(([path, data]) => ({
          path,
          name: path.split('/').pop(),
          count: data.count,
          symbols: [...new Set(data.symbols)].slice(0, 5)
        })),
        totalCount: externalDependents.size
      }
    });

    // Add edges from the external dependents node to visible nodes
    const targetsWithExternalDependents = new Set();
    edges.forEach(edge => {
      if (visibleNodeIds.has(edge.target) && externalDependents.has(edge.source)) {
        targetsWithExternalDependents.add(edge.target);
      }
    });
    targetsWithExternalDependents.forEach(target => {
      resultEdges.push({
        id: `edge-from-ext-${target}`,
        source: extDeptNodeId,
        target,
        type: 'smoothstep',
        style: { stroke: '#94a3b8', strokeWidth: 2, strokeDasharray: '5,5' },
        markerEnd: { type: 'arrowclosed' },
        data: { isExternal: true }
      });
    });
  }

  return { nodes: resultNodes, edges: resultEdges };
}

/**
 * Get top-level directories (for root view)
 */
export function getTopLevelPaths(nodes) {
  const allPaths = nodes.map(n => n.data?.path || n.id).filter(Boolean);
  return getDirectChildPaths(allPaths, null);
}

/**
 * Parse CKB symbol search results into a flat array of { id, name, kind, file }
 * Handles various CKB response formats (text content, JSON, arrays)
 */
export function parseSymbolSearchResults(result) {
  if (!result) return [];

  // If result is already an array of symbols
  if (Array.isArray(result)) {
    return result.map(item => ({
      id: item.id || item.symbolId || '',
      name: item.name || item.label || 'Unknown',
      kind: item.kind || 'symbol',
      file: item.file || item.location?.file || '',
    })).filter(s => s.id);
  }

  // Handle { result: ... } wrapper from API
  const data = result.result || result;

  // CKB returns content as array of text items
  if (Array.isArray(data)) {
    const symbols = [];
    for (const item of data) {
      const text = typeof item === 'string' ? item : item?.text || '';
      if (!text) continue;

      // Try to parse as JSON
      try {
        const parsed = JSON.parse(text);
        if (parsed.data?.results) {
          for (const r of parsed.data.results) {
            symbols.push({
              id: r.id || r.symbolId || '',
              name: r.name || 'Unknown',
              kind: r.kind || 'symbol',
              file: r.file || r.location?.file || '',
            });
          }
        } else if (Array.isArray(parsed)) {
          for (const r of parsed) {
            symbols.push({
              id: r.id || r.symbolId || '',
              name: r.name || 'Unknown',
              kind: r.kind || 'symbol',
              file: r.file || r.location?.file || '',
            });
          }
        }
      } catch {
        // Parse lines like "symbolId - name (kind) in file"
        const lines = text.split('\n');
        for (const line of lines) {
          const match = line.match(/^(ckb:\S+)\s*[-–]\s*(.+?)(?:\s*\((\w+)\))?\s*(?:in\s+(.+))?$/);
          if (match) {
            symbols.push({
              id: match[1],
              name: match[2].trim(),
              kind: match[3] || 'symbol',
              file: match[4] || '',
            });
          }
        }
      }
    }
    return symbols.filter(s => s.id);
  }

  // Handle object with data.results
  if (data?.data?.results) {
    return data.data.results.map(r => ({
      id: r.id || r.symbolId || '',
      name: r.name || 'Unknown',
      kind: r.kind || 'symbol',
      file: r.file || r.location?.file || '',
    })).filter(s => s.id);
  }

  return [];
}
